import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useSearchParams } from "react-router";
import { metaType } from "../../config/meta";
import { getString } from "../../utilities";
import { routeCodes } from "../../config/routes";
import { getMediaUrl, variousURLs, arcademicsIntroDuration } from "../../config";
import LoadingAnim from "../global/loadingAnim";
import Modal from "../global/modal";
import Confirm from "../global/confirm";
import MobileGameButtons from "./mobileGameButtons";
import ArcademicsHTML5 from "./arcademicsHTML5";
import { setMobileGame } from "../../redux/slices/fullPageSlice";
import { gameCanPlay, getGameAnalytics, getGameHTMLVersion, isFreeGame, requiresMotion } from "../../utilities/games";
import { useGameAbandonedHandler, useIframeSourceValidation } from "../../hooks";
import { setGame as setAnalyticsGame } from "../../redux/slices/gameSlice";
import { dataSelector } from "../../redux/slices/dataSlice";

export default function MobileGame({ shortname }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  useGameAbandonedHandler();

  const { lists } = useSelector(dataSelector);

  const [game, setGame] = useState(null);
  const [arcademics, setArcademics] = useState(false);
  const [homeModal, setHomeModal] = useState(false);
  const [searchParams] = useSearchParams();
  const gameVersionOverride = searchParams.get("gameVersion");
  const emptyMotionListener = () => null;
  const iframeRef = useRef(null);

  const motionRequest = async () => {
    if (requiresMotion(game?.shortname || shortname)) {
      // A devicemotion listener in the parent is required for the game to also regiester for
      // the events in iOS 26, so we add an empty listener if the permission has been granted
      const deviceMotionPermission = await DeviceMotionEvent.requestPermission();
      if (deviceMotionPermission === "granted") {
        window.addEventListener("devicemotion", emptyMotionListener);
        window.addEventListener("deviceorientation", emptyMotionListener);
      }
    }
  };

  useEffect(() => {
    const nextGame = location.state ? location.state.game : null;

    if (nextGame && nextGame.mobileActive) {
      const nextArcademics = Object.prototype.hasOwnProperty.call(location.state, "arcademics")
        ? location.state.arcademics
        : false;

      if (game?.shortname !== nextGame?.shortname) {
        setTimeout(() => {
          // set global variable for GTM tracking
          window.local_data.grades = nextGame.grades;
          window.local_data.categories = nextGame.categories;
        }, 100);

        setGame(nextGame);
        setArcademics(nextArcademics);
        dispatch(setAnalyticsGame(getGameAnalytics(nextGame, true)));
      }
    } else {
      // fallback if someone navigates directly to mobile game
      // and there is no location state present
      // game data comes from location state
      navigate(`${routeCodes.GAMES}${shortname}`, { replace: true });
    }
    motionRequest();

    return () => {
      window.removeEventListener("devicemotion", emptyMotionListener);
      window.removeEventListener("deviceorientation", emptyMotionListener);
    };
  }, [location]);

  useEffect(() => {
    if (game && lists?.length > 0 && !gameCanPlay(isFreeGame(lists, game.id))) {
      navigate(`${routeCodes.GAMES}${shortname}`, { replace: true });
    }
  }, [game, lists]);

  useEffect(() => {
    dispatch(setMobileGame({ mobileGame: true }));

    return () => {
      // component will unmount
      // reset/remove global variable for GTM tracking
      delete window.local_data.grades;
      delete window.local_data.categories;
      dispatch(setMobileGame({ mobileGame: false }));
    };
  }, []);

  const toggleHomeModal = () => {
    setHomeModal((prev) => !prev);
  };

  useIframeSourceValidation(iframeRef, game);

  const renderContent = () => {
    if (arcademics) {
      return (
        <ArcademicsHTML5
          gameName={game.arcademicsName}
          settings={{
            introURL: variousURLs.arcademics.intro,
            introDuration: arcademicsIntroDuration,
            endGameURL: `//${window.location.host}`,
          }}
        />
      );
    }

    return (
      <iframe
        ref={iframeRef}
        id="abcya-game-iframe"
        title={game.shortname}
        frameBorder="0"
        scrolling="no"
        allow="accelerometer *;magnetometer *;gyroscope *;"
        src={`${getMediaUrl()}/games/${game.shortname}/${getGameHTMLVersion(game, gameVersionOverride)}/index.html`}
      />
    );
  };

  if (game) {
    return (
      <>
        {metaType("VideoGame", game, location.pathname)}

        <div className="game-mobile-game">{renderContent()}</div>

        <MobileGameButtons game={game} home={toggleHomeModal} />

        <Modal doShow={homeModal} closeOnEscape={toggleHomeModal}>
          <Confirm
            confirm={() => {
              navigate(`${routeCodes.GAMES}${game.localeShortname || game.shortname}`);
            }}
            cancel={toggleHomeModal}
            button={getString("yes.0")}
            cancelButton={getString("no.0")}
          >
            <div className="modal-content-header">{getString("game.mobile.exit")}</div>
          </Confirm>
        </Modal>
      </>
    );
  }

  return <LoadingAnim position="fixed" />;
}
